import { useState } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Typography,
    Grid,
    Avatar,
    IconButton,
    Alert,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';

function flattenValidationErrors(errors) {
    if (!errors || typeof errors !== "object") return [];

    const messages = [];
    Object.keys(errors)
        .sort()
        .forEach((key) => {
            const value = errors[key];
            if (Array.isArray(value)) {
                value.forEach((msg) => {
                    if (msg != null && String(msg).trim() !== "") {
                        messages.push(String(msg));
                    }
                });
            } else if (value != null && String(value).trim() !== "") {
                messages.push(String(value));
            }
        });

    return [...new Set(messages)];
}

export default function UploadModal({ open, onClose }) {
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previews, setPreviews] = useState([]);

    const validationMessages = flattenValidationErrors(errors);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);

        const imageFiles = files.filter((file) => (file.type || "").startsWith("image/"));
        const rejectedCount = files.length - imageFiles.length;

        if (rejectedCount > 0) {
            setErrors({
                images: [
                    `Only image files are allowed. ${rejectedCount} non-image file${rejectedCount > 1 ? "s were" : " was"} ignored.`,
                ],
            });
        } else {
            setErrors({});
        }

        e.target.value = "";

        if (imageFiles.length === 0) return;

        const newFiles = [...selectedFiles, ...imageFiles];
        setSelectedFiles(newFiles);

        const newPreviews = [];
        newFiles.forEach((file, index) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                newPreviews[index] = reader.result;
                setPreviews([...newPreviews]);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleRemoveFile = (index) => {
        const newFiles = selectedFiles.filter((_, i) => i !== index);
        const newPreviews = previews.filter((_, i) => i !== index);
        setSelectedFiles(newFiles);
        setPreviews(newPreviews);
    };

    const handleSubmit = async () => {
        if (selectedFiles.length === 0) {
            setErrors({ images: ['Please select at least one image'] });
            return;
        }

        try {
            setUploading(true);
            setErrors({});

            const formData = new FormData();
            selectedFiles.forEach((file) => {
                formData.append('images[]', file);
            });

            await api.post("admin/gallery", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setSelectedFiles([]);
            setPreviews([]);
            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            } else if (e.response?.data?.message) {
                setErrors({ images: [e.response.data.message] });
            } else if (e.message) {
                setErrors({ images: [e.message] });
            }
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFiles([]);
        setPreviews([]);
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Upload Images</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <FormControl error={validationMessages.length > 0}>
                        <FormLabel htmlFor="images">Select Images</FormLabel>
                        <Button variant="outlined" component="label" sx={{ mt: 1 }}>
                            Choose Files
                            <input
                                type="file"
                                hidden
                                multiple
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </Button>
                    </FormControl>

                    {validationMessages.length > 0 && (
                        <Alert severity="error" sx={{ alignItems: 'flex-start' }}>
                            <Typography variant="subtitle2" sx={{ mb: validationMessages.length > 1 ? 0.5 : 0 }}>
                                {validationMessages.length === 1 ? 'Upload error' : 'Upload errors'}
                            </Typography>
                            {validationMessages.length === 1 ? (
                                <Typography variant="body2">{validationMessages[0]}</Typography>
                            ) : (
                                <List dense disablePadding>
                                    {validationMessages.map((msg, index) => (
                                        <ListItem key={`${index}-${msg}`} disableGutters sx={{ py: 0.25 }}>
                                            <ListItemText
                                                primary={msg}
                                                primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Alert>
                    )}

                    {selectedFiles.length > 0 && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Selected Images ({selectedFiles.length}):
                            </Typography>
                            <Grid container spacing={2}>
                                {selectedFiles.map((file, index) => (
                                    <Grid item xs={6} sm={4} md={3} key={index}>
                                        <Box sx={{ position: 'relative' }}>
                                            <Avatar
                                                variant="rounded"
                                                src={previews[index]}
                                                sx={{ width: '100%', height: 150 }}
                                            />
                                            <IconButton
                                                size="small"
                                                color="error"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                                }}
                                                onClick={() => handleRemoveFile(index)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={uploading || selectedFiles.length === 0}
                >
                    {uploading ? <CircularProgress size={24} /> : `Upload ${selectedFiles.length} Image(s)`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
